import {
  Button,
  FormNumber,
  FormPrice,
  FormRoot,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import type { VariantUpdateInput } from "../../domain/contracts";
import { useBulkUpdateVariants } from "../api/queries";

// ใส่ z.literal("") ก่อน เพื่อไม่ให้ค่าว่างถูก z.coerce.number() แปลงเป็น 0
const BulkEditFormSchema = z
  .object({
    price: z.union([z.literal(""), z.coerce.number().int().min(0)]).optional(),
    cost: z.union([z.literal(""), z.coerce.number().int().min(0)]).optional(),
    compareAtPrice: z
      .union([z.literal(""), z.coerce.number().int().min(0)])
      .optional(),
    weightGrams: z
      .union([z.literal(""), z.coerce.number().int().min(0)])
      .optional(),
  })
  .refine(
    (data) => {
      const p = data.price;
      const co = data.cost;
      const c = data.compareAtPrice;
      const w = data.weightGrams;
      return (
        (p !== undefined && p !== "") ||
        (co !== undefined && co !== "") ||
        (c !== undefined && c !== "") ||
        (w !== undefined && w !== "")
      );
    },
    { message: "ກະລຸນາໃສ່ຢ່າງໜ້ອຍ 1 ຊ່ອງ" },
  );

type BulkEditFormValues = z.infer<typeof BulkEditFormSchema>;

function toPayload(values: BulkEditFormValues): VariantUpdateInput {
  const payload: VariantUpdateInput = {};
  const priceNum =
    values.price !== undefined && values.price !== ""
      ? Number(values.price)
      : undefined;
  const costNum =
    values.cost !== undefined && values.cost !== ""
      ? Number(values.cost)
      : undefined;
  const compareNum =
    values.compareAtPrice !== undefined && values.compareAtPrice !== ""
      ? Number(values.compareAtPrice)
      : undefined;
  const weightNum =
    values.weightGrams !== undefined && values.weightGrams !== ""
      ? Number(values.weightGrams)
      : undefined;
  // ไม่ใส่ใน payload เมื่อค่าเป็น 0 เพราะ FormPrice/FormNumber ส่ง 0 มาเมื่อช่องว่าง
  if (priceNum !== undefined && priceNum !== 0) payload.price = priceNum;
  if (costNum !== undefined && costNum !== 0) payload.cost = costNum;
  if (compareNum !== undefined && compareNum !== 0)
    payload.compareAtPrice = compareNum;
  if (weightNum !== undefined && weightNum !== 0)
    payload.weightGrams = weightNum;
  return payload;
}

interface BulkEditVariantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  variantIds: string[];
  onSuccess?: () => void;
}

export function BulkEditVariantsDialog({
  open,
  onOpenChange,
  productId,
  variantIds,
  onSuccess,
}: BulkEditVariantsDialogProps) {
  const bulkUpdate = useBulkUpdateVariants(productId);

  const methods = RHF.useForm<BulkEditFormValues>({
    resolver: zodResolver(BulkEditFormSchema) as Resolver<BulkEditFormValues>,
    defaultValues: {
      price: undefined,
      cost: undefined,
      compareAtPrice: undefined,
      weightGrams: undefined,
    },
  });

  const onSubmit = (values: BulkEditFormValues) => {
    const payload = toPayload(values);
    if (Object.keys(payload).length === 0) return;
    bulkUpdate.mutate(
      { variantIds, payload },
      {
        onSuccess: () => {
          onSuccess?.();
          onOpenChange(false);
          methods.reset();
        },
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ແກ້ໄຂຫຼາຍ Variants</h3>
          <p className="text-muted-foreground text-sm">
            ຕັ້ງຄ່າດັ່ງຕໍ່ໄປນີ້ຈະຖືກນຳໃຊ້ກັບ {variantIds.length} variants ທີ່ເລືອກ
            (ຊ່ອງທີ່ວ່າງຈະບໍ່ຖືກປ່ຽນ)
          </p>

          <FormPrice
            currencySymbol="₭"
            name="price"
            label="ລາຄາ (LAK)"
            placeholder="ບໍ່ປ່ຽນ"
          />
          <FormPrice
            currencySymbol="₭"
            name="cost"
            label="ຕົ້ນທຶນ (LAK)"
            placeholder="ບໍ່ປ່ຽນ"
          />
          <FormPrice
            currencySymbol="₭"
            name="compareAtPrice"
            label="ລາຄາເກົ່າ (LAK)"
            placeholder="ບໍ່ປ່ຽນ"
          />
          <FormNumber
            name="weightGrams"
            label="ນ້ຳໜັກ (ກຣາມ)"
            placeholder="ບໍ່ປ່ຽນ"
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={bulkUpdate.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={bulkUpdate.isPending}>
              {bulkUpdate.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
